import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from '../components/customAxios';
import baseUrl from '../../base_url';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/common/StatusBadge';
import {
  Users,
  Search,
  Download,
  ExternalLink,
  Mail,
  Phone,
  Calendar,
  X,
  Trash2
} from 'lucide-react';

const AdminCustomers = () => {
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all, vip, repeat, first, inactive

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, ordersRes] = await Promise.allSettled([
        axios.get(`${baseUrl}api/admin/get-all-users`, { withCredentials: true }),
        axios.get(`${baseUrl}api/admin/get-orders`, { withCredentials: true })
      ]);

      if (usersRes.status === 'fulfilled') {
        setUsers(usersRes.value.data.data || []);
      } else {
        setError('Failed to fetch customers');
      }

      if (ordersRes.status === 'fulfilled') {
        setOrders(ordersRes.value.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching customers data:', err);
      setError('Failed to fetch customers data');
    } finally {
      setLoading(false);
    }
  };

  // Associate orders with customers and calculate metrics
  const customersWithMetrics = useMemo(() => {
    return users.map((user) => {
      const userOrders = orders.filter((order) => {
        const orderUserId = order.userId?._id || order.userId;
        const orderEmail = order.customer?.email || order.shippingAddress?.email || (order.address && order.address[0]?.email);
        return (orderUserId && String(orderUserId) === String(user._id)) ||
               (orderEmail && user.email && orderEmail.toLowerCase() === user.email.toLowerCase());
      });

      const totalSpend = userOrders.reduce((sum, order) => {
        if (['Cancelled', 'Refunded'].includes(order.orderStatus)) return sum;
        return sum + Number(order.pricing?.totalAmount ?? order.payment?.amount ?? order.financials?.netTotal ?? order.totalAmount ?? 0);
      }, 0);

      const orderCount = userOrders.length;
      const aov = orderCount > 0 ? totalSpend / orderCount : 0;

      // Sort user orders latest first
      const sortedOrders = [...userOrders].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      const lastOrderDate = sortedOrders[0]?.createdAt || null;

      let segment = 'Inactive';
      if (totalSpend >= 200) {
        segment = 'VIP';
      } else if (orderCount >= 2) {
        segment = 'Repeat';
      } else if (orderCount === 1) {
        segment = 'First-time';
      }

      return {
        ...user,
        fullName: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Guest Customer',
        orderCount,
        totalSpend,
        aov,
        lastOrderDate,
        segment,
        customerOrders: sortedOrders
      };
    });
  }, [users, orders]);

  // Tab filtering and search
  const filteredCustomers = useMemo(() => {
    return customersWithMetrics.filter((c) => {
      // Tab filter
      if (activeTab === 'vip' && c.segment !== 'VIP') return false;
      if (activeTab === 'repeat' && c.segment !== 'Repeat' && c.segment !== 'VIP') return false;
      if (activeTab === 'first' && c.segment !== 'First-time') return false;
      if (activeTab === 'inactive' && c.orderCount > 0) return false;

      // Search filter
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (
        c.fullName.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phoneNumber && c.phoneNumber.toLowerCase().includes(q)) ||
        c._id.toLowerCase().includes(q)
      );
    });
  }, [customersWithMetrics, activeTab, searchQuery]);

  // Summary Metrics
  const metrics = useMemo(() => {
    const totalCustomers = customersWithMetrics.length;
    const activeWithOrders = customersWithMetrics.filter((c) => c.orderCount > 0);
    const vipCount = customersWithMetrics.filter((c) => c.segment === 'VIP').length;
    const totalRevenue = customersWithMetrics.reduce((sum, c) => sum + c.totalSpend, 0);
    const avgLtv = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

    return {
      totalCustomers,
      activeCount: activeWithOrders.length,
      vipCount,
      totalRevenue,
      avgLtv
    };
  }, [customersWithMetrics]);

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await axios.post(`${baseUrl}api/admin/delete-user/${id}`, {}, { withCredentials: true });
      toast.success('Customer removed successfully');
      setUsers((prev) => prev.filter((u) => u._id !== id));
      if (selectedUser && selectedUser._id === id) {
        setSelectedUser(null);
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error('Failed to delete customer');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Customer ID', 'Full Name', 'Email', 'Phone', 'Orders Count', 'LTV (£)', 'AOV (£)', 'Segment', 'Last Order'];
    const rows = filteredCustomers.map((c) => [
      c._id,
      `"${c.fullName}"`,
      `"${c.email || ''}"`,
      `"${c.phoneNumber || ''}"`,
      c.orderCount,
      c.totalSpend.toFixed(2),
      c.aov.toFixed(2),
      c.segment,
      c.lastOrderDate ? new Date(c.lastOrderDate).toLocaleDateString() : 'Never'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `customers_report_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ').filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return parts[0] ? parts[0][0].toUpperCase() : '?';
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-stone-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-stone-900 tracking-tight">Customer Intelligence</h1>
            <span className="text-xs bg-stone-100 text-stone-600 font-mono px-2 py-0.5 rounded-full">
              {metrics.totalCustomers} Accounts
            </span>
          </div>
          <p className="text-xs text-stone-500 mt-0.5">
            Customer lifetime value, order frequency, cohort categorization, and purchase history.
          </p>
        </div>

        <button
          onClick={handleExportCSV}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-stone-300 text-stone-700 text-xs font-medium hover:bg-stone-50 transition-colors shadow-xs"
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Total Customers</div>
          <div className="text-2xl font-bold text-stone-900 mt-1 font-mono">{metrics.totalCustomers}</div>
          <div className="text-[11px] text-stone-500 mt-1">
            <span className="font-semibold text-emerald-600 font-mono">{metrics.activeCount}</span> with ≥1 order
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">VIP Customers (&gt;£200)</div>
          <div className="text-2xl font-bold text-amber-600 mt-1 font-mono">{metrics.vipCount}</div>
          <div className="text-[11px] text-stone-500 mt-1">High-value repeat purchasers</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Cumulative Spend</div>
          <div className="text-2xl font-bold text-stone-900 mt-1 font-mono">
            £{metrics.totalRevenue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">Across all completed orders</div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-stone-200 shadow-xs">
          <div className="text-[11px] font-semibold text-stone-500 uppercase tracking-wider">Average LTV</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1 font-mono">
            £{metrics.avgLtv.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="text-[11px] text-stone-500 mt-1">Per registered account</div>
        </div>
      </div>

      {/* Main Workspace Table & Filter Tabs */}
      <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        {/* Controls Toolbar */}
        <div className="p-4 border-b border-stone-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
            {[
              { id: 'all', label: 'All Customers', count: customersWithMetrics.length },
              { id: 'vip', label: 'VIP (>£200)', count: metrics.vipCount },
              { id: 'repeat', label: 'Repeat (2+)', count: customersWithMetrics.filter((c) => c.orderCount >= 2).length },
              { id: 'first', label: 'First-time', count: customersWithMetrics.filter((c) => c.orderCount === 1).length },
              { id: 'inactive', label: 'Inactive (0)', count: customersWithMetrics.filter((c) => c.orderCount === 0).length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                  activeTab === tab.id
                    ? 'bg-stone-900 text-white shadow-xs'
                    : 'text-stone-600 hover:bg-stone-100 hover:text-stone-900'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                  activeTab === tab.id ? 'bg-stone-800 text-stone-300' : 'bg-stone-100 text-stone-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search Box */}
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search name, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-xs bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-stone-400 focus:bg-white transition-all"
            />
          </div>
        </div>

        {/* Customer Table */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center text-stone-400">
            <div className="w-6 h-6 border-2 border-stone-300 border-t-amber-600 rounded-full animate-spin mb-3" />
            <span className="text-xs">Loading customer intelligence data...</span>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-rose-600 text-xs">{error}</div>
        ) : filteredCustomers.length === 0 ? (
          <div className="py-16 text-center text-stone-400">
            <Users className="w-8 h-8 mx-auto stroke-[1.5] mb-2 opacity-50" />
            <div className="text-sm font-medium text-stone-700">No customers found</div>
            <div className="text-xs text-stone-500 mt-0.5">Try refining your search terms or segment filter.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-stone-50/80 text-stone-500 border-b border-stone-200">
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Customer</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Segment</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">Orders</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">LTV (£)</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">AOV (£)</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px]">Last Order</th>
                  <th className="py-3 px-4 font-semibold uppercase tracking-wider text-[10px] text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {filteredCustomers.map((cust) => (
                  <tr
                    key={cust._id}
                    onClick={() => setSelectedUser(cust)}
                    className="hover:bg-amber-50/40 cursor-pointer transition-colors"
                  >
                    <td className="py-3.5 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-stone-100 border border-stone-200 text-stone-700 font-semibold flex items-center justify-center text-xs shrink-0">
                          {getInitials(cust.fullName)}
                        </div>
                        <div>
                          <div className="font-semibold text-stone-900">{cust.fullName}</div>
                          <div className="text-[11px] text-stone-500">{cust.email || 'No email'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        cust.segment === 'VIP'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : cust.segment === 'Repeat'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : cust.segment === 'First-time'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-stone-100 text-stone-500'
                      }`}>
                        {cust.segment}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono font-medium text-stone-900">
                      {cust.orderCount}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono font-bold text-stone-900">
                      £{cust.totalSpend.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 text-right font-mono text-stone-600">
                      £{cust.aov.toFixed(2)}
                    </td>
                    <td className="py-3.5 px-4 text-stone-500 text-[11px]">
                      {cust.lastOrderDate ? new Date(cust.lastOrderDate).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="py-3.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelectedUser(cust)}
                          className="p-1.5 rounded-md text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
                          title="View 360 Profile"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(cust._id)}
                          className="p-1.5 rounded-md text-stone-400 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Customer 360 Slide-over Profile Drawer */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-xs flex justify-end">
          <div className="bg-white w-full max-w-xl h-full shadow-2xl flex flex-col border-l border-stone-200 animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="p-5 border-b border-stone-200 flex items-center justify-between bg-stone-50/80">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-stone-900 text-amber-400 font-bold flex items-center justify-center text-sm shadow-xs">
                  {getInitials(selectedUser.fullName)}
                </div>
                <div>
                  <h2 className="text-base font-bold text-stone-900">{selectedUser.fullName}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[11px] text-stone-500 font-mono">ID: {selectedUser._id}</span>
                    <span className="text-stone-300">•</span>
                    <span className="text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.2 rounded-full border border-amber-200">
                      {selectedUser.segment}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setSelectedUser(null)}
                className="p-1.5 rounded-lg text-stone-400 hover:text-stone-700 hover:bg-stone-200/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Drawer Content */}
            <div className="flex-1 overflow-y-auto p-5 space-y-6">
              {/* Financial Metrics Cards */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-stone-50 p-3 rounded-xl border border-stone-200 text-center">
                  <div className="text-[10px] text-stone-500 uppercase font-semibold">Total Spend</div>
                  <div className="text-lg font-bold text-stone-900 font-mono mt-0.5">
                    £{selectedUser.totalSpend.toFixed(2)}
                  </div>
                </div>
                <div className="bg-stone-50 p-3 rounded-xl border border-stone-200 text-center">
                  <div className="text-[10px] text-stone-500 uppercase font-semibold">Orders Placed</div>
                  <div className="text-lg font-bold text-stone-900 font-mono mt-0.5">
                    {selectedUser.orderCount}
                  </div>
                </div>
                <div className="bg-stone-50 p-3 rounded-xl border border-stone-200 text-center">
                  <div className="text-[10px] text-stone-500 uppercase font-semibold">Average Order</div>
                  <div className="text-lg font-bold text-stone-900 font-mono mt-0.5">
                    £{selectedUser.aov.toFixed(2)}
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-stone-50/50 rounded-xl border border-stone-200 p-4 space-y-2.5">
                <div className="text-xs font-bold text-stone-800 uppercase tracking-wider text-[10px]">Contact Info</div>
                <div className="flex items-center gap-2 text-xs text-stone-700">
                  <Mail className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                  <span>{selectedUser.email || 'No email provided'}</span>
                  {selectedUser.email && (
                    <a
                      href={`mailto:${selectedUser.email}`}
                      className="text-amber-700 hover:underline text-[11px] ml-auto font-medium"
                    >
                      Email Customer
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-700">
                  <Phone className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                  <span>{selectedUser.phoneNumber || 'No phone recorded'}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-700">
                  <Calendar className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                  <span>Joined: {new Date(selectedUser.createdAt).toLocaleDateString()}</span>
                </div>
              </div>

              {/* Order History */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-bold text-stone-800 uppercase tracking-wider text-[10px]">
                    Purchase History ({selectedUser.customerOrders?.length || 0})
                  </div>
                </div>

                {selectedUser.customerOrders?.length === 0 ? (
                  <div className="p-4 rounded-xl bg-stone-50 border border-stone-200 text-center text-xs text-stone-500">
                    This customer has not placed any orders yet.
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {selectedUser.customerOrders.map((ord) => (
                      <div
                        key={ord._id}
                        className="p-3 bg-white rounded-xl border border-stone-200 hover:border-stone-300 shadow-xs flex items-center justify-between gap-3 transition-colors"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-xs text-stone-900">
                              #{ord._id.slice(-6).toUpperCase()}
                            </span>
                            <StatusBadge status={ord.orderStatus} />
                          </div>
                          <div className="text-[11px] text-stone-500 mt-1">
                            {new Date(ord.createdAt).toLocaleDateString()} • {ord.items?.length || 0} items
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <div className="font-mono font-bold text-xs text-stone-900">
                              £{(Number(ord.pricing?.totalAmount ?? ord.payment?.amount ?? ord.financials?.netTotal ?? ord.totalAmount ?? 0)).toFixed(2)}
                            </div>
                            <div className="text-[10px] text-stone-400">{ord.paymentMethod || 'Stripe'}</div>
                          </div>
                          <Link
                            to={`/order/${ord._id}`}
                            className="p-1.5 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-600 transition-colors"
                            title="Open Order Details"
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Drawer Footer */}
            <div className="p-4 border-t border-stone-200 bg-stone-50 flex items-center justify-between">
              <button
                onClick={() => handleDeleteUser(selectedUser._id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-rose-600 hover:bg-rose-50 text-xs font-semibold transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete Customer
              </button>

              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-1.5 bg-stone-900 text-white rounded-lg text-xs font-semibold hover:bg-stone-800 transition-colors shadow-xs"
              >
                Close Profile
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;